import React, { useState, useEffect, useRef } from "react";
import {
  Gamepad2, Monitor, Package, CreditCard,
  Utensils, Clock, BarChart3, Users,
  Trophy, UserCog, Shield,
  DollarSign, Lock, Zap, ArrowRight,
  X, Minus, Plus, Check,
  ChevronLeft, ChevronRight, LayoutGrid,
  TrendingUp, Star, Sparkles,
  Receipt, CheckCircle2, XCircle, AlertCircle,
  Play, Pause, Circle
} from "lucide-react";
import "./POSPage.css";
import TerminalShowcase from "./TerminalShowcase";

// Import screenshots
import activesessionImg from "./assets/images/pos/activesession.png";
import analyticsImg from "./assets/images/pos/analytics.png";
import consoledashImg from "./assets/images/pos/consoledash.png";
import financeDashImg from "./assets/images/pos/financeDash.png";
import handoversImg from "./assets/images/pos/handovers.png";
import inventoryImg from "./assets/images/pos/inventory.png";
import invoiceImg from "./assets/images/pos/invoice.png";

/* ── Data ─────────────────────────────────────────────────────── */
const posFeaturesData = [
  {
    id: "session-mgmt",
    icon: <Gamepad2 size={24} />,
    title: "Console & Session Management",
    summary: "The heart of the system. Manage every gaming station in real-time with live per-minute billing, pause/resume, and multi-player tracking.",
    whyItMatters: "Generic POS systems bill by item or table. Gaming cafes bill by time. Our per-minute, multi-player, pause-enabled session engine is built for exactly how your business works.",
    features: [
      { name: "Real-Time Console Dashboard", desc: "Live floor view of all consoles showing active/available status at a glance" },
      { name: "Per-Minute Billing", desc: "Automatic time-based billing with configurable per-minute rates for each console type" },
      { name: "Dynamic Console Types", desc: "Define unlimited console types (PS5, VR, Racing Sim, Snooker) each with its own rate and minimum charge" },
      { name: "Session Timer", desc: "Live running timer for every active session with accurate duration tracking" },
      { name: "Pause & Resume", desc: "Pause sessions when customers take breaks — billing stops automatically and resumes when they return" },
      { name: "Multi-Player Sessions", desc: "Up to 4 players per console session with individual tracking, billing, and food orders" },
      { name: "Player Join / Leave", desc: "Players can join or leave mid-session without affecting other players' billing" },
      { name: "Session Transfer", desc: "Seamlessly move a session from one console to another with full audit trail" },
      { name: "Walk-in Guest Support", desc: "Quick-start sessions for walk-in guests without requiring registration" },
      { name: "Minimum Session Charge", desc: "Set minimum billing amounts per console type regardless of duration" },
      { name: "Session Void", desc: "Void sessions with reason tracking — full audit trail maintained" },
      { name: "Session Audit Log", desc: "Every action recorded — start, close, add/remove player, transfer, void, payment, discount" }
    ]
  },
  {
    id: "pc-mgmt",
    icon: <Monitor size={24} />,
    title: "PC Session / Recharge Management",
    summary: "A completely separate workflow designed for cyber cafe / PC gaming operations with a pre-paid recharge model.",
    whyItMatters: "Most gaming cafes run both PCs and consoles. Other systems force you to use the same billing model for both. We built separate, optimized workflows for each.",
    features: [
      { name: "PC Recharge System", desc: "Pre-paid recharge model — customers load balance, system deducts per minute" },
      { name: "PC Separate from Console", desc: "PCs operate on recharge-based workflow while consoles use live per-minute billing" },
      { name: "Recharge Purchase Tracking", desc: "Every recharge logged with payment method, amount, and staff who processed it" },
      { name: "Balance Deduction Logs", desc: "Detailed per-session deduction history" },
      { name: "PC Revenue Analytics", desc: "PC-specific revenue tracked separately from console gaming" }
    ]
  },
  {
    id: "console-packages",
    icon: <Package size={24} />,
    title: "Console Minute Packages & Wallet",
    summary: "A loyalty-driving minute wallet system. Let regular customers pre-buy hours at discounted rates — system handles all wallet math automatically.",
    whyItMatters: "Packages drive repeat visits and upfront cash flow. Let your regular customers pre-buy hours at discounted rates — the system handles all the wallet math automatically.",
    features: [
      { name: "Console Packages", desc: "Create packages like 'PS5 — 5 Hours for ₹500' with configurable minutes and pricing" },
      { name: "Minute Wallet", desc: "Clients accumulate a minute balance that auto-deducts when they play" },
      { name: "Console-Type Restriction", desc: "Packages can be restricted to specific console types" },
      { name: "Expiry Dates", desc: "Set expiration windows (e.g., minutes expire in 30 days)" },
      { name: "Purchase History", desc: "Full audit trail of every package purchased" },
      { name: "Package Invoice Printing", desc: "Professional package purchase receipts for customers" }
    ]
  },
  {
    id: "billing-payment",
    icon: <CreditCard size={24} />,
    title: "Smart Billing & Payment System",
    summary: "Comprehensive billing with multi-payment support, credit system, and inclusive tax management on one bill.",
    whyItMatters: "Your billing system needs to handle time-based charges, food orders, package credits, multi-payment splits, and tax compliance — all on one bill.",
    features: [
      { name: "Session Bill Generation", desc: "Auto-calculated bills combining time cost + food/drink orders + recharges + packages" },
      { name: "Split Payment Support", desc: "Accept multiple payment methods on a single bill (e.g., ₹300 Cash + ₹200 UPI)" },
      { name: "Payment Methods", desc: "Cash, UPI, Card, Credit (Pay Later), and Staff Ledger Deduction" },
      { name: "Credit / Pay-Later System", desc: "Allow trusted clients to pay later — full credit ledger with running balance" },
      { name: "Configurable Discounts", desc: "Apply flat or percentage discounts at checkout with reason tracking" },
      { name: "Inclusive Tax System", desc: "VAT/GST calculated as inclusive — supports gaming tax, F&B tax, food tax, and recharge tax separately" },
      { name: "Tax Rate Snapshots", desc: "Tax rates frozen at time of payment for historical accuracy" },
      { name: "Session Invoice Printing", desc: "Professional, printable session invoices with full tax breakdown" }
    ]
  },
  {
    id: "food-inventory",
    icon: <Utensils size={24} />,
    title: "Food, Drinks & Inventory Management",
    summary: "Dual-source food management: in-house inventory and external restaurant partner orders from the same dashboard.",
    whyItMatters: "Gaming cafes aren't just about gaming — food and drinks are a major revenue stream. Our dual-source inventory system handles both your in-house snacks and restaurant partners seamlessly.",
    features: [
      { name: "In-House Inventory", desc: "Full stock management with SKU, cost price, selling price, and stock levels" },
      { name: "External Restaurant Menu", desc: "Integrate external food partners with their full menu" },
      { name: "Food Ordering from Session", desc: "Staff can add food/drink orders directly to a player's session bill" },
      { name: "Per-Player Orders", desc: "Food orders linked to individual players for accurate billing per person" },
      { name: "Stock Tracking", desc: "Real-time stock level monitoring with configurable reorder alerts" },
      { name: "Vendor Ledger", desc: "Running ledger of what you owe each food vendor" },
      { name: "Profit Margin Tracking", desc: "Per-item profit margin calculation (selling price vs. cost price)" }
    ]
  },
  {
    id: "shift-mgmt",
    icon: <Clock size={24} />,
    title: "Shift Management & Cash Handover",
    summary: "The accountability system that prevents cash leakage and staff disputes with 3-way reconciliation.",
    whyItMatters: "Cash leakage is the #1 profit killer for gaming cafes. Our handover system creates mathematical accountability — every rupee accounted for.",
    features: [
      { name: "Work Shifts", desc: "Track individual staff shifts with start/end times and financial snapshots" },
      { name: "Cash Denomination Counting", desc: "Staff counts physical cash by denomination — configurable for any currency" },
      { name: "Shift Handover System", desc: "Structured 3-way reconciliation: System Expected → Outgoing Staff Declaration → Incoming Staff Verification" },
      { name: "Discrepancy Detection", desc: "Automatic surplus/shortage calculation for every payment method" },
      { name: "Automatic Dispute Creation", desc: "System auto-creates disputes when handover items have verification gaps" },
      { name: "Cash Drawer Tracking", desc: "Full audit trail of every cash movement" },
      { name: "Multi-Currency Denomination Support", desc: "Configurable cash denominations for INR, AED, or any custom currency" }
    ]
  },
  {
    id: "analytics-bi",
    icon: <BarChart3 size={24} />,
    title: "Analytics & Business Intelligence",
    summary: "Data-driven insights with daily snapshots, staff performance analytics, and real-time financial dashboards.",
    whyItMatters: "You can't improve what you can't measure. Our analytics module gives you enterprise-grade business intelligence designed for gaming cafe operators.",
    features: [
      { name: "Daily Business Snapshots", desc: "Automated daily KPI snapshots: total sessions, players, revenue, expenses, net profit" },
      { name: "Revenue Breakdown", desc: "Separate tracking of gaming, PC, console, internal food, external food, and package revenue" },
      { name: "Staff Performance Analytics", desc: "Per-staff daily snapshots: sessions managed, revenue generated, items sold, discounts applied" },
      { name: "Inventory Item Analytics", desc: "Per-item daily snapshots: quantity sold, revenue, cost, profit, and margin percentage" },
      { name: "Collection Efficiency", desc: "Percentage of billed revenue actually collected" },
      { name: "Finance Dashboard", desc: "Dedicated financial overview with collections, expenses, credit movements, and cash position" }
    ]
  },
  {
    id: "client-crm",
    icon: <Users size={24} />,
    title: "Client Management (CRM)",
    summary: "Know your customers. Full session history, credit balances, minute wallets, and outstanding dues in one place.",
    whyItMatters: "Your regulars are your business. Know who they are, what they play, how much they spend, and what they owe — all in one place.",
    features: [
      { name: "Client Profiles", desc: "Searchable customer database with name, phone, and unique console UID" },
      { name: "Session History", desc: "Full history of every session a client has played" },
      { name: "Credit Balance", desc: "Running credit/debit ledger per client with full transaction history" },
      { name: "Minute Wallet Balance", desc: "Current console minute balance and purchase history" },
      { name: "Debtors & Creditors Dashboard", desc: "Dedicated view showing all outstanding balances and settlement history" }
    ]
  },
  {
    id: "loyalty-rewards",
    icon: <Trophy size={24} />,
    title: "Loyalty & Milestone Rewards",
    summary: "Drive repeat visits with automated milestone rewards. Set tiers, system tracks, owner approves — zero manual checking.",
    whyItMatters: "Loyalty programs increase customer retention by 25-30%. Our automated milestone system does the tracking for you.",
    features: [
      { name: "Customizable Milestone Ladder", desc: "Define your own reward tiers: Bronze at 200 hours, Silver at 500 hours, Gold at 1000 hours" },
      { name: "Automatic Eligibility Detection", desc: "System auto-detects when a client qualifies for a milestone" },
      { name: "Owner-Controlled Rewards", desc: "Rewards flagged as 'Pending' and only applied when Owner approves — prevents abuse" },
      { name: "Free Minutes Reward", desc: "Award free console gaming minutes as milestone rewards" },
      { name: "One-Time-Only Enforcement", desc: "Each client can only trigger each milestone once — enforced at database level" }
    ]
  },
  {
    id: "staff-roles",
    icon: <UserCog size={24} />,
    title: "Staff & Role Management",
    summary: "Hierarchical access control with granular permissions, staff liability ledgers, and full attribution on every action.",
    whyItMatters: "Your staff handles cash, processes payments, and manages sessions all day. You need to know exactly who did what, when, and how much they're accountable for.",
    features: [
      { name: "Role-Based Access", desc: "Three tiers: Owner (full access), Manager (operational access), Staff (session & sales access)" },
      { name: "Granular Permissions", desc: "Precise permission nodes: manage_sessions, view_reports, manage_inventory, handle_refunds" },
      { name: "Staff Liability Ledger", desc: "Financial ledger per staff tracking shortages, settlements, salary advances, and purchases" },
      { name: "Staff Gaming Tracking", desc: "Staff gaming sessions tracked via shadow client profiles — separating from customer revenue" },
      { name: "Staff Salary Recording", desc: "Base salary tracking and advance salary management" }
    ]
  },
  {
    id: "expense-mgmt",
    icon: <DollarSign size={24} />,
    title: "Operational Expense Management",
    summary: "Track every rupee going out. Categorized expenses with receipt management and cash drawer integration.",
    whyItMatters: "Expense tracking is critical for accurate net margin reporting. Instantly log maintenance, inventory purchases, and drawer loads.",
    features: [
      { name: "Expense Categories", desc: "Inventory Purchase, Staff Salary, Maintenance, Utilities, Marketing, and custom categories" },
      { name: "Payment Method Tracking", desc: "Record which payment method was used for each expense" },
      { name: "Receipt Management", desc: "Upload receipt documents (PDF, PNG, JPG) for each expense" },
      { name: "Cash Drawer Integration", desc: "Expenses paid via cash automatically reflected in the cash drawer balance" }
    ]
  }
];

const pricingPlans = [
  {
    name: "Professional",
    tagline: "Best for growing cafes & chains",
    price: "2,499",
    period: "month",
    limits: [
      { label: "Consoles", val: "Up to 12" },
      { label: "Staff Users", val: "Up to 6" },
      { label: "Active Sessions", val: "Up to 22" }
    ],
    features: [
      { name: "Session Management", enabled: true },
      { name: "Billing & Invoicing", enabled: true },
      { name: "Cash Handover System", enabled: true },
      { name: "Advanced Analytics", enabled: true },
      { name: "Loyalty Program", enabled: true },
      { name: "Expense Management", enabled: true },
      { name: "Priority Support", enabled: true }
    ],
    popular: true,
    cta: "Start Free Trial"
  },
  {
    name: "Enterprise",
    tagline: "For large franchises & networks",
    price: "8,999",
    period: "month",
    limits: [
      { label: "Consoles", val: "Unlimited" },
      { label: "Staff Users", val: "Unlimited" },
      { label: "Active Sessions", val: "Unlimited" }
    ],
    features: [
      { name: "Session Management", enabled: true },
      { name: "Billing & Invoicing", enabled: true },
      { name: "Cash Handover System", enabled: true },
      { name: "Advanced Analytics", enabled: true },
      { name: "Loyalty Program", enabled: true },
      { name: "Expense Management", enabled: true },
      { name: "Dedicated Account Manager", enabled: true }
    ],
    popular: false,
    cta: "Contact Sales"
  }
];

const posDifferentiators = [
  { icon: <Gamepad2 size={24} />, title: "Built for Gaming, Not Adapted", desc: "Per-minute billing, session pause/resume, multi-player management, console transfers — these aren't plugins, they're core features." },
  { icon: <Monitor size={24} />, title: "Dual PC + Console Support", desc: "The only POS that natively supports both recharge-based PC gaming and per-minute console billing in the same system." },
  { icon: <Shield size={24} />, title: "Cash Accountability at Scale", desc: "3-way reconciliation (System → Staff → Verification) and per-denomination cash counting eliminates the 'where did the money go?' problem." },
  { icon: <Lock size={24} />, title: "Zero Leakage Billing", desc: "Every minute tracked. Every food order linked to a player. Every payment attributed to a session. Nothing falls through the cracks." },
  { icon: <Utensils size={24} />, title: "F&B Revenue Stream Integration", desc: "Run your in-house snack bar AND manage external restaurant partnerships. Track vendor payables, commissions, and per-item profitability." },
  { icon: <UserCog size={24} />, title: "Staff Accountability", desc: "Know exactly who opened each session, who added each order, who processed each payment, and who caused each discrepancy." },
  { icon: <BarChart3 size={24} />, title: "Day-One Analytics", desc: "Every bill generates real-time data. Daily snapshots, staff performance, inventory analytics, and financial dashboards — from day one." }
];

const screenshotSlides = [
  { id: 1, title: "Console Dashboard", desc: "Live floor view of all stations", img: consoledashImg },
  { id: 2, title: "Active Session Detail", desc: "Live timer, players & food orders", img: activesessionImg },
  { id: 3, title: "Session Invoice", desc: "Auto-generated tax-inclusive bills", img: invoiceImg },
  { id: 4, title: "Finance Dashboard", desc: "Revenue, expenses & cash position", img: financeDashImg },
  { id: 5, title: "Shift Handover", desc: "3-way cash reconciliation panel", img: handoversImg },
  { id: 6, title: "Analytics Snapshot", desc: "Daily KPIs, staff rates & top items", img: analyticsImg },
  { id: 7, title: "Inventory Management", desc: "Stock logs, margins & alerts", img: inventoryImg }
];

const posFaqs = [
  { q: "Can I run this offline?", a: "Xynk Cloud POS is a cloud-based web application ensuring your data is instantly backed up and allowing real-time monitoring from anywhere. It requires an active internet connection." },
  { q: "What hardware is required?", a: "No expensive proprietary hardware needed! It runs in any modern web browser on PCs, laptops, iPads, tablets, or smartphones. Standard thermal receipt printers (ESC/POS) and USB barcode scanners work seamlessly." },
  { q: "How does Pause & Resume billing work?", a: "When a gamer takes a break, staff can pause the console session — billing stops instantly. When they return, clicking resume restarts the per-minute timer, tracking exact active playtime only." },
  { q: "Can different console types have different rates?", a: "Absolutely. Define unlimited console types (PS5, VR, Racing Sim, Snooker) and configure custom per-minute rates, minimum billing charges, and specific tax rates for each." },
  { q: "What happens during a shift handover dispute?", a: "If an incoming staff member records a physical cash drawer balance that doesn't match the system's expected balance, the system automatically flags a discrepancy and creates a dispute. Managers can review logs, assign liability, and resolve through ledgers." }
];

// eslint-disable-next-line no-unused-vars
const liveConsoles = [
  { id: "PS5-01", type: "PS5", player: "Rahul M.", time: "1:12:34", charge: "₹ 204", status: "active" },
  { id: "PS5-02", type: "PS5", player: "Priya S.", time: "0:34:10", charge: "₹ 102", status: "active" },
  { id: "VR-01", type: "VR Box", player: "Vikram K.", time: "0:22:05", charge: "₹ 155", status: "active" },
  { id: "XBOX-01", type: "Xbox", player: "—", time: "—", charge: "—", status: "available" },
  { id: "SIM-01", type: "Racing SIM", player: "Arjun D.", time: "0:45:00", charge: "₹ 180", status: "paused" },
  { id: "PS5-03", type: "PS5", player: "Dev T.", time: "0:08:20", charge: "₹ 25", status: "active" }
];

/* ── AnimatedCounter ────────────────────────────────────────── */
function AnimatedCounter({ target, prefix = "", suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasRun = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasRun.current) {
        hasRun.current = true;
        const numericTarget = parseFloat(target.replace(/[^0-9.]/g, ""));
        const startTime = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * numericTarget));
          if (progress < 1) requestAnimationFrame(tick);
          else setCount(numericTarget);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref} className="stat-number">{prefix}{count}{suffix}</span>;
}

/* ── ConsoleCard ────────────────────────────────────────────── */
function ConsoleCard({ console: c, index, onClick }) {
  const statusConfig = {
    active: { 
      label: "Active", 
      dotColor: "#10b981", 
      textColor: "#10b981"
    },
    available: { 
      label: "Available", 
      dotColor: "#6b7280", 
      textColor: "#9ca3af"
    },
    paused: { 
      label: "Paused", 
      dotColor: "#f59e0b", 
      textColor: "#fbbf24"
    }
  };
  
  const s = statusConfig[c.status];
  
  const formatSecondsToTime = (secs) => {
    if (secs === 0 || !secs) return "0:00:00";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const getRate = (type) => {
    if (type.includes("PS5")) return 3;
    if (type.includes("VR")) return 7;
    if (type.includes("SIM")) return 4;
    return 3;
  };

  const calculateCharge = (c) => {
    if (c.status === "available") return 0;
    const rate = getRate(c.type);
    const minutes = (c.secondsElapsed || 0) / 60;
    return Math.ceil(minutes * rate);
  };
  
  return (
    <div 
      className={`console-card status-${c.status}`}
      onClick={onClick}
      style={{
        animationDelay: `${index * 50}ms`
      }}
    >
      <div className="console-card-header">
        <span className="console-id">
          {c.id}
        </span>
        <span className="console-status" style={{ color: s.textColor }}>
          <span className="console-dot" />
          {s.label}
        </span>
      </div>
      <div className="console-type">
        {c.type}
      </div>
      <div className="console-player">
        {c.status === "available" ? "Ready to Play" : c.player}
      </div>
      {c.status !== "available" && (
        <div className="console-footer">
          <span className="console-time">
            {formatSecondsToTime(c.secondsElapsed)}
          </span>
          <span className="console-charge" style={{ color: s.dotColor }}>
            ₹{calculateCharge(c)}
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────── */
export default function POSPage() {
  const [expandedModule, setExpandedModule] = useState("session-mgmt");
  const [faqOpen, setFaqOpen] = useState({});

  // --- Mobile Swipe Tabs System ---
  const tabsRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50; 
    const isRightSwipe = distance < -50; 

    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = posFeaturesData.findIndex(m => m.id === expandedModule);
      if (currentIndex !== -1) {
        let nextIndex = currentIndex;
        if (isLeftSwipe && currentIndex < posFeaturesData.length - 1) {
          nextIndex = currentIndex + 1;
        } else if (isRightSwipe && currentIndex > 0) {
          nextIndex = currentIndex - 1;
        }
        
        if (nextIndex !== currentIndex) {
          const nextModId = posFeaturesData[nextIndex].id;
          handleTabClick(nextModId);
        }
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleTabClick = (id) => {
    setExpandedModule(id);
    const container = tabsRef.current;
    if (container) {
      const btn = container.querySelector(`[data-id="${id}"]`);
      if (btn) {
        isProgrammaticScroll.current = true;
        
        const containerRect = container.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        const targetScrollLeft = container.scrollLeft + (btnRect.left - containerRect.left) - (containerRect.width - btnRect.width) / 2;
        
        container.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });

        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          isProgrammaticScroll.current = false;
        }, 350);
      }
    }
  };

  const handleTabsScroll = () => {
    if (!tabsRef.current || isProgrammaticScroll.current) return;
    
    const isMobile = window.innerWidth <= 1024;
    if (!isMobile) return;

    const container = tabsRef.current;
    const buttons = container.querySelectorAll('.feature-tab-btn');
    if (buttons.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestBtn = null;
    let minDistance = Infinity;

    buttons.forEach((btn) => {
      const rect = btn.getBoundingClientRect();
      const btnCenter = rect.left + rect.width / 2;
      const distance = Math.abs(btnCenter - containerCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestBtn = btn;
      }
    });

    if (closestBtn) {
      const activeId = closestBtn.getAttribute('data-id');
      if (activeId && activeId !== expandedModule) {
        setExpandedModule(activeId);
      }
    }
  };
  const [activeDemoTab, setActiveDemoTab] = useState("floor");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", interest: "Start Free Trial", message: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const carouselRef = useRef(null);

  // --- Interactive Dashboard Simulator States ---
  const [consoles, setConsoles] = useState([
    { id: "PS5-01", type: "PS5", player: "Rahul M.", secondsElapsed: 4354, status: "active", foodItems: [{ name: "Cheese Maggi", price: 90 }, { name: "Pepsi", price: 40 }] },
    { id: "PS5-02", type: "PS5", player: "Priya S.", secondsElapsed: 2050, status: "active", foodItems: [] },
    { id: "VR-01", type: "VR Box", player: "Vikram K.", secondsElapsed: 1325, status: "active", foodItems: [] },
    { id: "XBOX-01", type: "Xbox", player: "—", secondsElapsed: 0, status: "available", foodItems: [] },
    { id: "SIM-01", type: "Racing SIM", player: "Arjun D.", secondsElapsed: 2700, status: "paused", foodItems: [] },
    { id: "PS5-03", type: "PS5", player: "Dev T.", secondsElapsed: 500, status: "active", foodItems: [] }
  ]);
  const [selectedConsoleId, setSelectedConsoleId] = useState(null);
  const [checkoutConsoleId, setCheckoutConsoleId] = useState(null);
  const [revenueToday, setRevenueToday] = useState(14250);
  const [sessionsCount, setSessionsCount] = useState(47);
  const [notification, setNotification] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("UPI");

  // New session input states
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newDeviceType, setNewDeviceType] = useState("PS5");

  // Format Helper
  const formatSecondsToTime = (secs) => {
    if (secs === 0 || !secs) return "0:00:00";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const getRate = (type) => {
    if (type.includes("PS5")) return 3;
    if (type.includes("VR")) return 7;
    if (type.includes("SIM")) return 4;
    return 3; // default rate
  };

  const calculateCharge = (c) => {
    if (c.status === "available") return 0;
    const rate = getRate(c.type);
    const minutes = (c.secondsElapsed || 0) / 60;
    return Math.ceil(minutes * rate);
  };

  // Toast helper
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Action Handlers
  const handleStartSession = (id, playerName, deviceType) => {
    if (!playerName.trim()) {
      showNotification("Please enter player name", "error");
      return;
    }
    setConsoles(prev => prev.map(c => {
      if (c.id === id) {
        return {
          ...c,
          player: playerName,
          type: deviceType,
          status: "active",
          secondsElapsed: 0,
          foodItems: []
        };
      }
      return c;
    }));
    setNewPlayerName("");
    showNotification(`Session started for ${playerName}!`);
  };

  const handleTogglePause = (id) => {
    let newStatus = "";
    setConsoles(prev => prev.map(c => {
      if (c.id === id) {
        newStatus = c.status === "active" ? "paused" : "active";
        return { ...c, status: newStatus };
      }
      return c;
    }));
    showNotification(`Session ${newStatus === "active" ? "resumed" : "paused"}!`);
  };

  const handleAddFood = (id, name, price) => {
    setConsoles(prev => prev.map(c => {
      if (c.id === id) {
        return {
          ...c,
          foodItems: [...c.foodItems, { name, price }]
        };
      }
      return c;
    }));
    showNotification(`Added ${name} (₹${price})!`);
  };

  const handleCheckout = (id) => {
    const c = consoles.find(item => item.id === id);
    if (!c) return;
    const consoleBill = calculateCharge(c);
    const foodTotal = c.foodItems.reduce((sum, item) => sum + item.price, 0);
    const total = consoleBill + foodTotal;

    setRevenueToday(prev => prev + total);
    setSessionsCount(prev => prev + 1);

    setConsoles(prev => prev.map(item => {
      if (item.id === id) {
        return {
          ...item,
          player: "—",
          status: "available",
          secondsElapsed: 0,
          foodItems: []
        };
      }
      return item;
    }));

    setCheckoutConsoleId(null);
    setSelectedConsoleId(null);
    showNotification(`Settled! Collected ₹${total}`);
  };

  // Timer TICK loop
  useEffect(() => {
    const interval = setInterval(() => {
      setConsoles(prevConsoles =>
        prevConsoles.map(c => {
          if (c.status === "active") {
            return { ...c, secondsElapsed: c.secondsElapsed + 1 };
          }
          return c;
        })
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.body.classList.add("pos-page-active");
    return () => {
      document.body.classList.remove("pos-page-active");
    };
  }, []);

  /* ── Scroll-triggered reveal animations ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );

    const elements = document.querySelectorAll(".reveal-on-scroll");
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* ── Scroll progress bar ── */
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      const bar = document.querySelector(".scroll-progress");
      if (bar) bar.style.width = `${progress}%`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleFaq = (i) => setFaqOpen(p => ({ ...p, [i]: !p[i] }));
  // eslint-disable-next-line no-unused-vars
  const toggleModule = (id) => setExpandedModule(p => p === id ? null : id);

  const scrollCarousel = (dir) => {
    if (carouselRef.current) {
      const slideWidth = carouselRef.current.querySelector(".pos-ss-slide")?.clientWidth || 340;
      carouselRef.current.scrollBy({ left: dir * slideWidth, behavior: "smooth" });
    }
  };

  const handleCarouselScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const slideWidth = carouselRef.current.querySelector(".pos-ss-slide")?.clientWidth || 340;
      const newIndex = Math.round(scrollLeft / slideWidth);
      if (newIndex >= 0 && newIndex < screenshotSlides.length) {
        setActiveSlideIndex(newIndex);
      }
    }
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch("https://formspree.io/f/xwvwdywl", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => setFormSubmitted(false), 5000);
        setFormData({ name: "", email: "", phone: "", interest: "Start Free Trial", message: "" });
      } else {
        alert("Something went wrong. Please try again or contact us directly.");
      }
    } catch (error) {
      alert("Error submitting form. Please check your internet connection.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pos-page-wrapper">
      <div className="scroll-progress" />

      {/* Hero floating particles */}
      <div className="hero-particles">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="hero-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${8 + Math.random() * 12}s`,
              animationDelay: `${Math.random() * 10}s`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
            }}
          />
        ))}
      </div>

      {/* ══════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════ */}
      <section className="pos-section hero-section-centered">
        <div className="grid-bg" />
        <div className="pos-container">
          <div className="pos-hero-grid">
            <div className="hero-centered-content pos-animate-fade-up">
            <div className="hero-badges-center">
              <span className="cyber-badge">PURPOSE-BUILT POS</span>
              <span className="cyber-badge cyber-badge-dot">
                <Circle size={8} fill="#10b981" /> v4.2 Live
              </span>
            </div>
            
            <h1 className="pos-title-hero">
              The Operating System for <br />
              <span className="pos-text-gradient-cyan">Gaming Cafes</span>
            </h1>
            
            <p className="hero-description-center">
              Purpose-built POS for gaming cafes, cyber cafes, and esports lounges. 
              Manage sessions, billing, inventory, staff shifts, and analytics — all 
              from one intuitive dashboard.
            </p>

            <div className="hero-cta-center">
              <a href="#contact" className="cyber-btn primary-btn">
                Start Free Trial
                <ArrowRight size={18} />
              </a>
              <a href="#contact" className="cyber-btn cyber-btn-outline">
                <Play size={16} /> Watch Demo
              </a>
            </div>

            <div className="hero-trust-center">
              <div className="rating">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#4f46e5" color="#4f46e5" />
                ))}
              </div>
              <span className="trust-text">Trusted by <strong>54+</strong> gaming cafes worldwide</span>
            </div>
            
            <p className="hero-note-center">
              <CheckCircle2 size={14} color="#10b981" /> 
              No credit card required · Setup in under 15 minutes
            </p>
            <TerminalShowcase />
          </div>

          {/* Centered Massive Dashboard Mockup */}
          <div className="dashboard-mockup-wrapper pos-animate-fade-up pos-animate-fade-up-delay-2">
            <div className="dashboard-preview-card">
                {/* Window Chrome */}
                <div className="dashboard-header">
                  <div className="window-dots">
                    <span className="dot dot-red" />
                    <span className="dot dot-yellow" />
                    <span className="dot dot-green" />
                  </div>
                  <span className="dashboard-title">Xynk Cloud · Live Dashboard</span>
                  <div className="live-indicator">
                    <span className="live-dot" />
                    <span>LIVE</span>
                  </div>
                </div>

                {/* Tab Bar */}
                <div className="dashboard-tabs">
                  {[
                    { id: "floor", label: "Live Floor", icon: <LayoutGrid size={14} /> },
                    { id: "finances", label: "Finances", icon: <TrendingUp size={14} /> },
                    { id: "shifts", label: "Shifts", icon: <Clock size={14} /> }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveDemoTab(tab.id)}
                      className={`dashboard-tab ${activeDemoTab === tab.id ? 'active' : ''}`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Dashboard Content */}
                <div className="dashboard-content" style={{ position: "relative" }}>
                  {/* Stats Row */}
                  <div className="stats-grid">
                    {[
                      { label: "Active", value: `${consoles.filter(c => c.status === "active").length}/${consoles.length}`, color: "#818cf8" },
                      { label: "Revenue Today", value: `₹${(revenueToday / 1000).toFixed(1)}k`, color: "#f59e0b" },
                      { label: "Sessions", value: `${sessionsCount}`, color: "#10b981" }
                    ].map((stat, i) => (
                      <div key={i} className="stat-card">
                        <div className="stat-value" style={{ color: stat.color }}>
                          {stat.value}
                        </div>
                        <div className="stat-label">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Floor View */}
                  {activeDemoTab === "floor" && (
                    <div style={{ position: "relative" }}>
                      {notification && (
                        <div className="notification-toast">
                          <Sparkles size={14} style={{ color: "#10b981" }} />
                          <span>{notification.message}</span>
                        </div>
                      )}

                      {!selectedConsoleId && !checkoutConsoleId && (
                        <div>
                          <div className="section-label">Console Floor (Click Card to Settle / Edit)</div>
                          <div className="console-grid">
                            {consoles.map((c, i) => (
                              <ConsoleCard 
                                key={c.id} 
                                console={c} 
                                index={i} 
                                onClick={() => setSelectedConsoleId(c.id)} 
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedConsoleId && !checkoutConsoleId && (
                        (() => {
                          const selectedConsole = consoles.find(c => c.id === selectedConsoleId);
                          if (!selectedConsole) return null;
                          const consoleBill = calculateCharge(selectedConsole);
                          const foodTotal = selectedConsole.foodItems.reduce((sum, item) => sum + item.price, 0);
                          const totalBill = consoleBill + foodTotal;
                          
                          return (
                            <div className="console-detail-view">
                              <div className="console-detail-header">
                                <button className="back-btn" onClick={() => setSelectedConsoleId(null)}>
                                  <ChevronLeft size={16} /> Back to Floor
                                </button>
                              </div>
                              
                              <div className="console-detail-body">
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                                  <div>
                                    <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>Station {selectedConsole.id}</h3>
                                    <div className="console-type" style={{ margin: 0 }}>{selectedConsole.type}</div>
                                  </div>
                                  <span className={`console-status status-${selectedConsole.status}`}>
                                    <span className="console-dot" />
                                    {selectedConsole.status}
                                  </span>
                                </div>

                                {selectedConsole.status === "available" ? (
                                  <div className="new-session-form">
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                      <label>Player Name</label>
                                      <input 
                                        type="text" 
                                        placeholder="Enter name (e.g. Rahul)" 
                                        value={newPlayerName} 
                                        onChange={(e) => setNewPlayerName(e.target.value)} 
                                      />
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                      <label>Device Type</label>
                                      <select 
                                        value={newDeviceType} 
                                        onChange={(e) => setNewDeviceType(e.target.value)}
                                      >
                                        <option value="PS5">PS5 (₹3/min)</option>
                                        <option value="Xbox">Xbox (₹3/min)</option>
                                        <option value="VR Box">VR Box (₹7/min)</option>
                                        <option value="Racing SIM">Racing SIM (₹4/min)</option>
                                      </select>
                                    </div>
                                    <button 
                                      className="btn-action-primary" 
                                      onClick={() => handleStartSession(selectedConsole.id, newPlayerName, newDeviceType)}
                                    >
                                      <Play size={14} /> Start Session
                                    </button>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="console-detail-info-grid">
                                      <div className="info-box">
                                        <span>Gamer</span>
                                        <strong>{selectedConsole.player}</strong>
                                      </div>
                                      <div className="info-box">
                                        <span>Time Elapsed</span>
                                        <strong className="mono">{formatSecondsToTime(selectedConsole.secondsElapsed)}</strong>
                                      </div>
                                      <div className="info-box">
                                        <span>Gaming Fee</span>
                                        <strong className="mono">₹{consoleBill}</strong>
                                      </div>
                                      <div className="info-box">
                                        <span>Food Bill</span>
                                        <strong className="mono">₹{foodTotal}</strong>
                                      </div>
                                    </div>

                                    <div className="food-orders-section">
                                      <h4>Snacks & Refreshments</h4>
                                      {selectedConsole.foodItems.length > 0 ? (
                                        <div className="food-items-list">
                                          {selectedConsole.foodItems.map((item, idx) => (
                                            <div key={idx} className="food-item-row">
                                              <span>{item.name}</span>
                                              <span className="food-item-price">₹{item.price}</span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p style={{ fontSize: "12px", color: "#9ca3af", margin: "0 0 12px 0", fontStyle: "italic" }}>
                                          No snacks ordered yet
                                        </p>
                                      )}
                                      
                                      <div className="food-btn-grid">
                                        <button className="food-add-btn" onClick={() => handleAddFood(selectedConsole.id, "Pepsi", 40)}>
                                          + Pepsi (₹40)
                                        </button>
                                        <button className="food-add-btn" onClick={() => handleAddFood(selectedConsole.id, "Maggi", 90)}>
                                          + Maggi (₹90)
                                        </button>
                                        <button className="food-add-btn" onClick={() => handleAddFood(selectedConsole.id, "Monster", 110)}>
                                          + Monster (₹110)
                                        </button>
                                      </div>
                                    </div>

                                    <div className="console-detail-actions">
                                      <button 
                                        className="btn-action-secondary" 
                                        onClick={() => handleTogglePause(selectedConsole.id)}
                                      >
                                        {selectedConsole.status === "active" ? <Pause size={16} /> : <Play size={16} />}
                                      </button>
                                      <button 
                                        className="btn-action-primary" 
                                        onClick={() => setCheckoutConsoleId(selectedConsole.id)}
                                      >
                                        <Receipt size={14} /> Settle (₹{totalBill})
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()
                      )}

                      {checkoutConsoleId && (
                        (() => {
                          const checkoutConsole = consoles.find(c => c.id === checkoutConsoleId);
                          if (!checkoutConsole) return null;
                          const consoleBill = calculateCharge(checkoutConsole);
                          const foodTotal = checkoutConsole.foodItems.reduce((sum, item) => sum + item.price, 0);
                          const totalBill = consoleBill + foodTotal;

                          return (
                            <div className="checkout-view">
                              <div className="console-detail-header">
                                <button className="back-btn" onClick={() => setCheckoutConsoleId(null)}>
                                  <ChevronLeft size={16} /> Back
                                </button>
                              </div>

                              <div className="console-detail-body">
                                <h3 style={{ margin: "0 0 4px 0", fontSize: "16px", fontWeight: "700" }}>Settle Station {checkoutConsole.id}</h3>
                                <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 12px 0" }}>
                                  Gamer: {checkoutConsole.player} · Setup: {checkoutConsole.type}
                                </p>

                                <div className="checkout-summary">
                                  <div className="checkout-row">
                                    <span>Console Fee ({formatSecondsToTime(checkoutConsole.secondsElapsed)})</span>
                                    <span className="mono">₹{consoleBill}</span>
                                  </div>
                                  <div className="checkout-row">
                                    <span>Snacks & Items ({checkoutConsole.foodItems.length})</span>
                                    <span className="mono">₹{foodTotal}</span>
                                  </div>
                                  <div className="checkout-row total">
                                    <span>Total Collected</span>
                                    <span className="mono">₹{totalBill}</span>
                                  </div>
                                </div>

                                <div style={{ margin: "12px 0" }}>
                                  <label style={{ fontSize: "10px", textTransform: "uppercase", color: "#9ca3af", fontWeight: "700", display: "block", marginBottom: "6px" }}>
                                    Payment Method
                                  </label>
                                  <div className="payment-methods">
                                    {["UPI", "Cash", "Card"].map(method => (
                                      <button 
                                        key={method}
                                        className={`payment-btn ${selectedPaymentMethod === method ? "active" : ""}`}
                                        onClick={() => setSelectedPaymentMethod(method)}
                                      >
                                        {method}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                <button 
                                  className="btn-action-primary" 
                                  onClick={() => handleCheckout(checkoutConsole.id)}
                                  style={{ background: "#10b981", width: "100%", marginTop: "8px" }}
                                >
                                  <Check size={14} /> Confirm Settlement
                                </button>
                              </div>
                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}

                  {/* Finances View */}
                  {activeDemoTab === "finances" && (
                    <div className="finances-view">
                      <div className="section-label">Today's Breakdown</div>
                      {[
                        { label: "Console Gaming", amt: `₹${(revenueToday * 0.59).toFixed(0)}`, pct: 59, color: "#6366f1" },
                        { label: "F&B Sales", amt: `₹${(revenueToday * 0.23).toFixed(0)}`, pct: 23, color: "#f59e0b" },
                        { label: "PC Recharges", amt: `₹${(revenueToday * 0.18).toFixed(0)}`, pct: 18, color: "#10b981" }
                      ].map((item, i) => (
                        <div key={i} className="finance-item">
                          <div className="finance-header">
                            <span className="finance-label">{item.label}</span>
                            <span className="finance-amount" style={{ color: item.color }}>
                              {item.amt}
                            </span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${item.pct}%`, background: item.color }} 
                            />
                          </div>
                        </div>
                      ))}
                      <div className="total-row">
                        <span>Total Collected Today</span>
                        <span className="total-amount">₹{revenueToday}</span>
                      </div>
                    </div>
                  )}

                  {/* Shifts View */}
                  {activeDemoTab === "shifts" && (
                    <div className="shifts-view">
                      <div className="section-label">Current Shift Status</div>
                      <div className="shift-info-card">
                        <div className="shift-row">
                          <span>Active Staff</span>
                          <span className="shift-value">Amit K. (Morning)</span>
                        </div>
                        <div className="shift-row">
                          <span>Shift Duration</span>
                          <span className="shift-value mono">6h 34m</span>
                        </div>
                      </div>
                      {[
                        { label: "Cash Expected", sys: `₹${(revenueToday * 0.58).toFixed(0)}`, match: true },
                        { label: "UPI Collected", sys: `₹${(revenueToday * 0.26).toFixed(0)}`, match: true },
                        { label: "Card Payments", sys: `₹${(revenueToday * 0.16).toFixed(0)}`, match: true }
                      ].map((item, i) => (
                        <div key={i} className="verification-row">
                          <span className="verification-label">{item.label}</span>
                          <div className="verification-details">
                            <span className="verification-amount">{item.sys}</span>
                            <span className="match-badge">
                              <CheckCircle2 size={12} /> MATCH
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* ══════════════════════════════════════
          STATS STRIP
      ═══════════════════════════════════════ */}
      <div className="stats-strip">
        <div className="pos-container">
          <div className="stats-strip-grid reveal-on-scroll">
            {[
              { num: "54", suf: "+", label: "Gaming Cafes", sublabel: "Across Globe" },
              { num: "1000000", pre: "", suf: "+", label: "Sessions Tracked", sublabel: "Live and historical" },
              { num: "99.9", suf: "%", label: "Uptime SLA", sublabel: "Cloud-reliable" },
              { num: "15", suf: " min", label: "Setup Time", sublabel: "First outlet live" }
            ].map((s, i) => (
              <div key={i} className="stat-item">
                <div className="stat-number-wrapper">
                  <AnimatedCounter target={String(s.num)} prefix={s.pre || ""} suffix={s.suf} />
                </div>
                <div className="stat-item-label">{s.label}</div>
                <div className="stat-item-sublabel">{s.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          FEATURE MARQUEE
      ═══════════════════════════════════════ */}
      <div className="pos-marquee-section">
        <div className="pos-marquee-wrapper">
          <div className="pos-marquee-track">
            {[
              { text: "Console & Session Management", icon: <Gamepad2 size={16} /> },
              { text: "Per-Minute Billing System", icon: <Clock size={16} /> },
              { text: "PC Recharges & Wallet", icon: <Monitor size={16} /> },
              { text: "Split-Payment Cash Ledger", icon: <CreditCard size={16} /> },
              { text: "Inventory & F&B Orders", icon: <Utensils size={16} /> },
              { text: "Dual-Reconciliation Handovers", icon: <Users size={16} /> },
              { text: "Business Revenue Analytics", icon: <BarChart3 size={16} /> },
              { text: "Multi-Player Billing Tracks", icon: <Trophy size={16} /> },
              { text: "Staff Permissions Audit", icon: <UserCog size={16} /> },
              { text: "99.9% Uptime Guarantee", icon: <Shield size={16} /> }
            ].concat([
              { text: "Console & Session Management", icon: <Gamepad2 size={16} /> },
              { text: "Per-Minute Billing System", icon: <Clock size={16} /> },
              { text: "PC Recharges & Wallet", icon: <Monitor size={16} /> },
              { text: "Split-Payment Cash Ledger", icon: <CreditCard size={16} /> },
              { text: "Inventory & F&B Orders", icon: <Utensils size={16} /> },
              { text: "Dual-Reconciliation Handovers", icon: <Users size={16} /> },
              { text: "Business Revenue Analytics", icon: <BarChart3 size={16} /> },
              { text: "Multi-Player Billing Tracks", icon: <Trophy size={16} /> },
              { text: "Staff Permissions Audit", icon: <UserCog size={16} /> },
              { text: "99.9% Uptime Guarantee", icon: <Shield size={16} /> }
            ]).map((item, idx) => (
              <div key={idx} className="pos-marquee-item">
                <span className="pos-marquee-icon">{item.icon}</span>
                <span className="pos-marquee-text">{item.text}</span>
                <span className="pos-marquee-dot">•</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="glow-divider" />

      {/* ══════════════════════════════════════
          PROBLEM / SOLUTION
      ═══════════════════════════════════════ */}
      <section className="pos-section">
        <div className="pos-container">
          <div className="pos-section-header reveal-on-scroll">
            <span className="cyber-badge cyber-badge-dot">The Difference</span>
            <h2 className="pos-title-lg">
              Why gaming cafes need <span className="pos-text-gradient-cyan">purpose-built</span> tools
            </h2>
            <p className="pos-subtitle-md">
              Generic restaurant POS systems don't understand per-minute billing, pause/resume sessions, 
              or shift handover reconciliation. We built the right tool for the job.
            </p>
          </div>

          <div className="comparison-grid reveal-on-scroll">
            {/* Negative */}
            <div className="comparison-card negative">
              <div className="comparison-header">
                <XCircle size={24} color="#ef4444" />
                <h3>Generic Restaurant / Retail POS</h3>
              </div>
              <ul className="comparison-list">
                {[
                  "Bills by tables or items — no live per-minute timer",
                  "No native multiplayer session tracking or billing",
                  "No prepaid PC recharge wallet model",
                  "Cash-only handover tracking — easy to manipulate",
                  "No pause/resume billing for break times",
                  "No gaming-specific analytics or KPIs"
                ].map((item, i) => (
                  <li key={i} className="comparison-item negative">
                    <X size={16} color="#ef4444" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Positive */}
            <div className="comparison-card positive">
              <div className="comparison-header">
                <CheckCircle2 size={24} color="#6366f1" />
                <h3>Xynk Cloud POS</h3>
              </div>
              <ul className="comparison-list">
                {[
                  "Per-minute live billing engine scoped to console type",
                  "Up to 4 players per console — tracked and billed independently",
                  "Integrated PC recharge system & minute package wallet",
                  "3-way shift handover: System → Staff → Verified",
                  "Pause/resume billing with zero-gap time tracking",
                  "Gaming-native analytics, KPIs, and daily snapshots"
                ].map((item, i) => (
                  <li key={i} className="comparison-item positive">
                    <Check size={16} color="#6366f1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* ══════════════════════════════════════
          CORE MODULES GRID
      ═══════════════════════════════════════ */}
      <section className="pos-section modules-section">
        <div className="pos-container">
          <div className="pos-section-header reveal-on-scroll">
            <span className="cyber-badge cyber-badge-dot">Core Modules</span>
            <h2 className="pos-title-lg">
              Everything you need to <span className="pos-text-gradient-cyan">scale operations</span>
            </h2>
            <p className="pos-subtitle-md">
              14 purpose-built modules covering every aspect of gaming cafe operations.
            </p>
          </div>

          {/* Swipe indicator for mobile */}
          <div className="swipe-indicator-bar">
            <div className="swipe-indicator-line" />
            <div className="swipe-indicator-text-wrap">
              <span className="swipe-arrow">←</span>
              <span className="swipe-text">Swipe to explore modules</span>
              <span className="swipe-arrow">→</span>
            </div>
            <div className="swipe-indicator-line" />
          </div>

          <div className="pos-feat-grid reveal-on-scroll">
            {posFeaturesData.map((mod) => (
              <div key={mod.id} className="cyber-card pos-feat-card">
                <div className="pos-feat-icon-wrap">
                  {mod.icon}
                </div>
                <h3 className="feature-title">{mod.title}</h3>
                <p className="feature-summary">{mod.summary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          DEEP FEATURE ACCORDION
      ═══════════════════════════════════════ */}
      <section className="pos-section features-splitscreen-section" id="features">
        <div className="pos-container">
          <div className="pos-section-header reveal-on-scroll">
            <span className="cyber-badge">Full Feature List</span>
            <h2 className="pos-title-lg">
              Detailed feature <span className="pos-text-gradient-cyan">breakdown</span>
            </h2>
            <p className="pos-subtitle-md">
              Select a module from the panel to explore the complete feature set, workflows, and implementation details.
            </p>
          </div>

          <div className="features-swipe-indicator">
            <span className="swipe-arrow-left">←</span>
            <span>Swipe tabs or card to change module</span>
            <span className="swipe-arrow-right">→</span>
          </div>

          <div className="features-splitscreen-container">
            {/* Left Column: Vertical Tabs */}
            <div 
              className="features-tabs-sidebar"
              ref={tabsRef}
              onScroll={handleTabsScroll}
            >
              {posFeaturesData.map((mod) => (
                <button
                  key={mod.id}
                  data-id={mod.id}
                  className={`feature-tab-btn ${expandedModule === mod.id ? "active" : ""}`}
                  onClick={() => handleTabClick(mod.id)}
                >
                  <span className="tab-btn-icon">{mod.icon}</span>
                  <div className="tab-btn-text">
                    <span className="tab-btn-title">{mod.title}</span>
                    <span className="tab-btn-subtitle">{mod.features.length} Features</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Right Column: Details Panel */}
            <div 
              className="features-details-panel"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {(() => {
                const activeMod = posFeaturesData.find(m => m.id === expandedModule) || posFeaturesData[0];
                return (
                  <div className="features-details-inner" key={activeMod.id}>
                    <div className="details-panel-header">
                      <div className="details-title-wrap">
                        <span className="details-module-icon">{activeMod.icon}</span>
                        <div>
                          <h3>{activeMod.title}</h3>
                          <p>{activeMod.summary}</p>
                        </div>
                      </div>
                    </div>

                    <div className="details-why-matters">
                      <div className="why-matters-title">
                        <AlertCircle size={16} /> WHY IT MATTERS
                      </div>
                      <p>{activeMod.whyItMatters}</p>
                    </div>

                    <div className="details-features-grid-wrapper">
                      <h4>Module Features ({activeMod.features.length})</h4>
                      <div className="details-features-grid">
                        {activeMod.features.map((feat, fi) => (
                          <div key={fi} className="detail-feature-card">
                            <span className="feature-card-dot" />
                            <div>
                              <strong>{feat.name}</strong>
                              <p>{feat.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          DIFFERENTIATORS
      ═══════════════════════════════════════ */}
      <section className="pos-section differentiators-section">
        <div className="pos-container">
          <div className="pos-section-header reveal-on-scroll">
            <span className="cyber-badge cyber-badge-dot">Why Choose Us</span>
            <h2 className="pos-title-lg">
              Built different, <span className="pos-text-gradient-cyan">by design</span>
            </h2>
          </div>

          <div className="pos-diff-grid reveal-on-scroll">
            {posDifferentiators.map((d, i) => (
              <div key={i} className="pos-diff-card cyber-card">
                <div className="pos-diff-icon">{d.icon}</div>
                <div className="pos-diff-body">
                  <h4>{d.title}</h4>
                  <p>{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          SCREENSHOTS CAROUSEL
      ═══════════════════════════════════════ */}
      <section className="pos-section">
        <div className="pos-container">
          <div className="carousel-header">
            <div>
              <span className="cyber-badge cyber-badge-dot">Product Walkthrough</span>
              <h2 className="pos-title-lg">
                Every screen, <span className="pos-text-gradient-cyan">purpose-designed</span>
              </h2>
            </div>
            <div className="carousel-controls">
              <button onClick={() => scrollCarousel(-1)} className="carousel-btn">
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => scrollCarousel(1)} className="carousel-btn">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div ref={carouselRef} className="pos-ss-carousel" onScroll={handleCarouselScroll}>
            {screenshotSlides.map((slide) => (
              <div key={slide.id} className="pos-ss-slide">
                <div className="pos-ss-img-container">
                  <div className="pos-ss-mockup-header">
                    <div className="pos-ss-dots">
                      <span className="pos-ss-dot dot-red" />
                      <span className="pos-ss-dot dot-yellow" />
                      <span className="pos-ss-dot dot-green" />
                    </div>
                    <span className="pos-ss-mockup-title">Xynk Cloud · {slide.title}</span>
                  </div>
                  <div 
                    className="pos-ss-img-wrap" 
                    onClick={() => setSelectedImage({ src: slide.img, title: slide.title })}
                  >
                    <img src={slide.img} alt={slide.title} className="pos-ss-img" loading="lazy" />
                    <div className="pos-ss-zoom-overlay">
                      <Sparkles size={20} />
                      <span>Click to expand</span>
                    </div>
                  </div>
                </div>
                <div className="pos-ss-meta">
                  <h4>{slide.title}</h4>
                  <p>{slide.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          PRICING
      ═══════════════════════════════════════ */}
      <section className="pos-section pricing-section" id="pricing">
        <div className="pos-container">
          <div className="pos-section-header reveal-on-scroll">
            <span className="cyber-badge cyber-badge-dot">Pricing</span>
            <h2 className="pos-title-lg">
              Simple, <span className="pos-text-gradient-cyan">transparent</span> pricing
            </h2>
            <p className="pos-subtitle-md">
              No hidden fees. No setup costs. Cancel anytime.
            </p>
          </div>

          <div className="pos-price-grid reveal-on-scroll">
            {pricingPlans.map((plan, idx) => (
              <div 
                key={idx} 
                className={plan.popular ? "pricing-card featured" : "pricing-card"}
              >
                {plan.popular && (
                  <span className="popular-badge">✦ Most Popular</span>
                )}
                
                <div className="pricing-header">
                  <h4 className="plan-name">{plan.name}</h4>
                  <p className="plan-tagline">{plan.tagline}</p>
                </div>

                <div className="pricing-amount">
                  <span className="currency">₹</span>
                  <span className="price">{plan.price}</span>
                  <span className="period">/{plan.period}</span>
                </div>

                <div className="limits-box">
                  {plan.limits.map((lim, li) => (
                    <div key={li} className="limit-row">
                      <span className="limit-label">{lim.label}</span>
                      <span className="limit-value">{lim.val}</span>
                    </div>
                  ))}
                </div>

                <div className="features-list">
                  {plan.features.map((feat, fi) => (
                    <div key={fi} className={`feature-item ${feat.enabled ? '' : 'disabled'}`}>
                      {feat.enabled ? (
                        <Check size={18} color="#6366f1" />
                      ) : (
                        <Minus size={18} color="#d1d5db" />
                      )}
                      <span>{feat.name}</span>
                    </div>
                  ))}
                </div>

                <a href="#contact" className={`cyber-btn ${plan.popular ? 'primary-btn' : 'cyber-btn-outline'}`} style={{ width: "100%" }}>
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FAQ
      ═══════════════════════════════════════ */}
      <section className="pos-section" id="faq">
        <div className="pos-container" style={{ maxWidth: "780px" }}>
          <div className="pos-section-header reveal-on-scroll">
            <span className="cyber-badge cyber-badge-dot">FAQ</span>
            <h2 className="pos-title-lg">
              Common <span className="pos-text-gradient-cyan">questions</span>
            </h2>
          </div>

          <div className="pos-faq-list reveal-on-scroll">
            {posFaqs.map((faq, idx) => {
              const isOpen = faqOpen[idx];
              return (
                <div key={idx} className={`pos-faq-item ${isOpen ? "open" : ""}`}>
                  <button className="pos-faq-trigger" onClick={() => toggleFaq(idx)}>
                    <span className="faq-question">{faq.q}</span>
                    <span className="pos-faq-trigger-icon">
                      {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                    </span>
                  </button>
                  <div className="pos-faq-content" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                    <div style={{ overflow: "hidden" }}>
                      <div className="pos-faq-inner">{faq.a}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FINAL CTA + CONTACT FORM
      ═══════════════════════════════════════ */}
      <section className="pos-section cta-section" id="contact">
        <div className="pos-container">
          <div className="pos-cta-grid">
            {/* Left - Testimonial & CTA */}
            <div className="cta-content">
              <span className="cyber-badge cyber-badge-dot" style={{ marginBottom: "24px" }}>Get Started Today</span>
              <h2 className="pos-title-lg" style={{ marginBottom: "16px" }}>
                Ready to transform your <span className="pos-text-gradient-cyan">gaming cafe operations</span>?
              </h2>
              <p className="cta-description">
                Join 54+ gaming cafes that have already upgraded to Xynk Cloud POS. 
                Setup takes 15 minutes, and our team guides you through every step.
              </p>

              <div className="testimonial-card">
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
                  ))}
                </div>
                <p className="testimonial-text">
                  "Xynk Cloud POS transformed our operations. Our shift discrepancies dropped 
                  to zero and our monthly revenue tracking became effortless."
                </p>
                <div className="testimonial-author">
                  <div className="author-avatar">RM</div>
                  <div>
                    <h5>Rajesh M.</h5>
                    <p>GameZone Hyderabad</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Contact Form */}
            <div className="pos-contact-card">
              <div className="pos-contact-card-header-bar">
                <span>Secure Connection</span>
                <div className="pos-contact-card-status">
                  <span className="pos-contact-card-status-dot" />
                  <span>System Ready</span>
                </div>
              </div>

              {formSubmitted ? (
                <div className="success-message">
                  <CheckCircle2 size={56} color="#10b981" />
                  <h3>Application Submitted!</h3>
                  <p>Our team will contact you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-header">
                    <h3>Request Access</h3>
                    <p>Deploy the terminal in your cafe.</p>
                  </div>

                  <div className="pos-form-grid">
                    <div className="pos-form-group">
                      <label>Full Name</label>
                      <input 
                        type="text" 
                        required 
                        placeholder="Rajesh Kumar" 
                        value={formData.name}
                        onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} 
                      />
                    </div>
                    <div className="pos-form-group">
                      <label>Email Address</label>
                      <input 
                        type="email" 
                        required 
                        placeholder="you@cafe.com" 
                        value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} 
                      />
                    </div>
                  </div>

                  <div className="pos-form-group">
                    <label>Phone Number</label>
                    <input 
                      type="tel" 
                      required 
                      placeholder="+91 98765 43210" 
                      value={formData.phone}
                      onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} 
                    />
                  </div>

                  <div className="pos-form-group">
                    <label>Cafe Details</label>
                    <textarea 
                      rows={4} 
                      placeholder="Tell us about your cafe — consoles, PCs, current setup..."
                      value={formData.message}
                      onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} 
                    />
                  </div>

                  <button type="submit" className="cyber-btn primary-btn submit-btn" disabled={submitting}>
                    <Zap size={20} /> {submitting ? "Sending..." : "Submit Application"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="pos-lightbox-backdrop" onClick={() => setSelectedImage(null)}>
          <button className="pos-lightbox-close" onClick={() => setSelectedImage(null)}>
            <X size={24} />
          </button>
          <div className="pos-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage.src} alt={selectedImage.title} className="pos-lightbox-img" />
            <div className="pos-lightbox-caption">
              <h4>{selectedImage.title}</h4>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}